import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import {
  parseStudioVisit,
  readStudioVisit,
  saveStudioVisit,
  studioResumeKey,
  studioVisitInputSchema,
} from './studio-resume';
import { StudioResumeContext } from './studio-resume-context';

import type { StudioVisitInput } from './studio-resume';
import type { ReactNode } from 'react';

function AccountStudioResume({ userId, children }: { userId: string | null; children: ReactNode }) {
  const [visit, setVisit] = useState(() => readStudioVisit(userId));
  const active = useRef(true);
  useLayoutEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  const remember = useCallback(
    (input: StudioVisitInput) => {
      if (!userId || !active.current) return;
      const parsed = studioVisitInputSchema.safeParse(input);
      if (!parsed.success) return;
      const next = { ...parsed.data, visitedAt: new Date().toISOString() };
      setVisit(next);
      saveStudioVisit(userId, next);
    },
    [userId],
  );

  useEffect(() => {
    if (!userId) return;
    const receive = (event: StorageEvent) => {
      if (event.key !== studioResumeKey(userId)) return;
      const next = parseStudioVisit(event.newValue);
      if (!next) return;
      setVisit((previous) =>
        !previous || Date.parse(next.visitedAt) > Date.parse(previous.visitedAt) ? next : previous,
      );
    };
    window.addEventListener('storage', receive);
    return () => window.removeEventListener('storage', receive);
  }, [userId]);

  const value = useMemo(() => ({ visit, remember }), [visit, remember]);
  return <StudioResumeContext.Provider value={value}>{children}</StudioResumeContext.Provider>;
}

/** App navigation retains memory; account changes discard it before exposing the next account. */
export function StudioResumeProvider({
  userId,
  children,
}: {
  userId: string | null;
  children: ReactNode;
}) {
  return (
    <AccountStudioResume key={userId ?? 'anonymous'} userId={userId}>
      {children}
    </AccountStudioResume>
  );
}
